import type { ReactNode } from 'react';
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { ActionFooter } from './action-footer';
import {
  SHEET_BREAKPOINTS,
  SHEET_INITIAL_BREAKPOINT,
  SHEET_TOOLBAR_CLASS,
  sheetClass,
} from './sheet';

/*
 * 底部面板分兩層：
 *
 * - `SheetModal`：常駐的 IonModal 外殼，以 `isOpen` 控制開闔
 * - `SheetLayout`：標題列、捲動內容、底部操作鍵，放在面板的內容元件裡
 *
 * 分開是為了讓面板的狀態（步驟、表單）住在內容元件裡：IonModal 的內容在開啟時才掛載、
 * 關閉時卸載，所以每次開啟狀態都是乾淨的，不需要 key 或手動 reset。標題常隨步驟改變，
 * 所以標題列也得在內容元件裡。
 */

export interface SheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * 面板高度，例如 `'[--height:70%]'`。**必須是呼叫端寫下的字面值**，
   * 理由見 `sheetClass`。
   */
  heightClass: string;
  /** 傳 false 時擋住下拉關閉，用在送出或處理中，避免結果回來時面板已經不在 */
  canDismiss?: boolean;
  children: ReactNode;
}

export function SheetModal({
  isOpen,
  onClose,
  heightClass,
  canDismiss = true,
  children,
}: SheetModalProps) {
  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      breakpoints={SHEET_BREAKPOINTS}
      initialBreakpoint={SHEET_INITIAL_BREAKPOINT}
      canDismiss={canDismiss}
      handle
      className={sheetClass(heightClass)}
    >
      {children}
    </IonModal>
  );
}

export interface SheetLayoutProps {
  title: string;
  /** 標題列左側（通常是返回上一步） */
  headerStart?: ReactNode;
  /** 標題列右側的動作 */
  headerEnd?: ReactNode;
  /** 固定在底部的操作鍵（見 `ActionFooter`） */
  footer?: ReactNode;
  children: ReactNode;
}

export function SheetLayout({
  title,
  headerStart,
  headerEnd,
  footer,
  children,
}: SheetLayoutProps) {
  return (
    <>
      <IonHeader className="ion-no-border">
        <IonToolbar className={SHEET_TOOLBAR_CLASS}>
          {headerStart && <IonButtons slot="start">{headerStart}</IonButtons>}
          <IonTitle className="font-display text-base text-strong">
            {title}
          </IonTitle>
          {headerEnd && <IonButtons slot="end">{headerEnd}</IonButtons>}
        </IonToolbar>
      </IonHeader>

      {/* 內距寫在內容而非 IonContent：`ion-padding-*` 來自本專案未引入的 padding.css */}
      <IonContent>
        <div className="flex flex-col gap-5 px-4 pt-2 pb-6">{children}</div>
      </IonContent>

      {footer && <ActionFooter>{footer}</ActionFooter>}
    </>
  );
}
